import {
  Alert,
  Box,
  Button,
  Center,
  Loader,
  LoadingOverlay,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconRefresh } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useGetBaseAnalytics } from '../api/getBaseAnalytics';
import { useGetBaseExperts } from '../api/getBaseExperts';
import { ControlsPanel } from '../components/Base/ControlsPanel';
import { DynamicsSection } from '../components/Base/DynamicsSection';
import { KpiSections } from '../components/Base/KpiSections';
import { ProductsSection } from '../components/Base/ProductsSection';
import { BaseUnlockModal } from '../components/Base/UnlockModal';
import { BASE_LABELS, REGION_LABELS } from '../constants/base';
import { analyticsTypes } from '../types';
import { getCurrencySymbol, getPresetRange } from '../utils/base';

import { queryClient } from '@/libraries/react-query';
import storage from '@/utils/storage';

const AUTH_TOKEN_STORAGE_KEY = 'ezocrm_storage_token';

const isBaseAnalyticsLockedError = (error: unknown) => {
  const response = (error as { response?: { status?: number; data?: { code?: string } } } | null)
    ?.response;
  return response?.status === 403 && response?.data?.code === 'BASE_ANALYTICS_LOCKED';
};

const clearBaseAnalyticsQueries = () => {
  queryClient.removeQueries({ queryKey: ['analytics-base-experts'] });
  queryClient.removeQueries({ queryKey: ['analytics-base'] });
};

export const Base = () => {
  const navigate = useNavigate();
  const [isUnlocked, setIsUnlocked] = useState(() =>
    Boolean(storage.getAnalyticsBaseUnlockToken())
  );
  const [unlockModalOpen, setUnlockModalOpen] = useState(true);

  const lockBaseAnalytics = useCallback(() => {
    storage.clearAnalyticsBaseUnlockToken();
    clearBaseAnalyticsQueries();
    setIsUnlocked(false);
    setUnlockModalOpen(true);
  }, []);

  // JWT lives in shared localStorage; unlock token is per-tab sessionStorage.
  // Re-login in another tab updates auth without clearing unlock — re-prompt here.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== AUTH_TOKEN_STORAGE_KEY) {
        return;
      }
      lockBaseAnalytics();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [lockBaseAnalytics]);

  const handleDismissUnlock = () => {
    setUnlockModalOpen(false);
    // Direct open of /analytics/base has no in-app history; navigate(-1) → about:blank.
    const historyIdx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof historyIdx === 'number' && historyIdx > 0) {
      navigate(-1);
      return;
    }
    navigate('/', { replace: true });
  };

  const handleUnlocked = () => {
    // Drop cached 403s — refetchOnMount:false would remount into them and re-lock.
    clearBaseAnalyticsQueries();
    setIsUnlocked(true);
  };

  if (!isUnlocked) {
    return (
      <Box>
        <Title order={2} mb="lg">
          {BASE_LABELS.TITLE}
        </Title>
        <BaseUnlockModal
          opened={unlockModalOpen}
          onUnlocked={handleUnlocked}
          onClose={handleDismissUnlock}
        />
      </Box>
    );
  }

  return <BaseDashboard onLock={lockBaseAnalytics} />;
};

const BaseDashboard = ({ onLock }: { onLock: () => void }) => {
  const [expertId, setExpertId] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState(() => getPresetRange('30')[0]);
  const [dateTo, setDateTo] = useState(() => getPresetRange('30')[1]);

  const expertsQuery = useGetBaseExperts();
  const experts = useMemo(() => expertsQuery.data || [], [expertsQuery.data]);

  useEffect(() => {
    if (!expertId && experts[0]) {
      setExpertId(experts[0].id);
    }
  }, [expertId, experts]);

  const reportQuery = useGetBaseAnalytics({
    expertId: expertId || '',
    dateFrom: dateFrom.valueOf(),
    dateTo: dateTo.valueOf(),
  });

  useEffect(() => {
    if (
      isBaseAnalyticsLockedError(expertsQuery.error) ||
      isBaseAnalyticsLockedError(reportQuery.error)
    ) {
      onLock();
    }
  }, [expertsQuery.error, reportQuery.error, onLock]);

  const selectedExpert = useMemo(
    () => experts.find((expert) => expert.id === expertId),
    [experts, expertId]
  );

  const onPeriodChange = (from: Date, to: Date) => {
    setDateFrom(from);
    setDateTo(to);
  };

  return (
    <Box style={{ minWidth: 0, maxWidth: '100%' }}>
      <Stack gap={4} mb="lg">
        <Title order={2}>{BASE_LABELS.TITLE}</Title>
        <Text size="sm" c="dimmed">
          {buildContextLine(selectedExpert)}
        </Text>
      </Stack>

      <ControlsPanel
        experts={experts}
        expertId={expertId}
        dateFrom={dateFrom}
        dateTo={dateTo}
        isExpertsLoading={expertsQuery.isLoading}
        onExpertChange={setExpertId}
        onPeriodChange={onPeriodChange}
      />

      <Box mt="xl">{renderBody(expertsQuery, reportQuery, expertId)}</Box>
    </Box>
  );
};

const buildContextLine = (expert?: analyticsTypes.BaseExpertListItem) => {
  if (!expert) {
    return BASE_LABELS.PICK_EXPERT;
  }
  const baseNames = expert.basePages.map((page) => page.name).filter(Boolean);
  const baseNamesJoined = baseNames.join(', ');
  // Brand row often reuses a base page title as name — don't list that page twice.
  const title =
    !expert.name || expert.name === baseNamesJoined || baseNames.includes(expert.name)
      ? baseNamesJoined || expert.name
      : [expert.name, baseNamesJoined].filter(Boolean).join(' · ');
  const currency = `${expert.currency}${
    getCurrencySymbol(expert.currency) ? ` (${getCurrencySymbol(expert.currency)})` : ''
  }`;
  return [title, REGION_LABELS[expert.region], currency, BASE_LABELS.CONTEXT_NO_BREAKDOWN]
    .filter(Boolean)
    .join(' · ');
};

// Backend always returns a full report object, so emptiness is a fact about the numbers.
const isEmptyReport = (report: analyticsTypes.BaseAnalyticsReport) => {
  const hasLeads =
    report.keyMetrics.leadsToBase > 0 ||
    report.dynamics.leadsToBaseByDay.some((point) => point.value > 0);
  return report.summary.totalChecks === 0 && !report.keyMetrics.activeDialogs && !hasLeads;
};

const ErrorState = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
  <Center py="xl">
    <Alert
      icon={<IconAlertCircle size={16} />}
      title={BASE_LABELS.LOAD_ERROR}
      color="red"
      variant="light"
      maw={480}
      w="100%"
    >
      <Stack gap="sm">
        <Text size="sm">{message}</Text>
        <Button
          leftSection={<IconRefresh size={16} />}
          variant="light"
          color="red"
          onClick={onRetry}
        >
          {BASE_LABELS.RETRY}
        </Button>
      </Stack>
    </Alert>
  </Center>
);

const LockedPending = () => (
  <Center py="xl">
    <Loader size="xl" />
  </Center>
);

const renderBody = (
  expertsQuery: ReturnType<typeof useGetBaseExperts>,
  reportQuery: ReturnType<typeof useGetBaseAnalytics>,
  expertId: string | null
) => {
  // Disabled report query stays isLoading=true in RQ v4 — never gate on it before expertId.
  if (expertsQuery.isLoading) {
    return (
      <Center py="xl">
        <Loader size="xl" />
      </Center>
    );
  }

  if (expertsQuery.isError) {
    if (isBaseAnalyticsLockedError(expertsQuery.error)) {
      return <LockedPending />;
    }
    return (
      <ErrorState message={BASE_LABELS.LOAD_EXPERTS_ERROR} onRetry={() => expertsQuery.refetch()} />
    );
  }

  if (!expertId) {
    return <Center py="xl">{BASE_LABELS.PICK_EXPERT}</Center>;
  }

  if (reportQuery.isInitialLoading) {
    return (
      <Center py="xl">
        <Loader size="xl" />
      </Center>
    );
  }

  if (reportQuery.isError) {
    if (isBaseAnalyticsLockedError(reportQuery.error)) {
      return <LockedPending />;
    }
    return (
      <ErrorState message={BASE_LABELS.LOAD_REPORT_ERROR} onRetry={() => reportQuery.refetch()} />
    );
  }

  const payload = reportQuery.data;
  if (!payload?.data) {
    return <Center py="xl">{BASE_LABELS.EMPTY_PERIOD}</Center>;
  }

  const report = payload.data;
  const isDialogsAvailable =
    payload.meta?.dialogsAvailable ?? report.keyMetrics.activeDialogs !== null;

  if (isEmptyReport(report)) {
    return <Center py="xl">{BASE_LABELS.EMPTY_PERIOD}</Center>;
  }

  return (
    // keepPreviousData shows the previous expert/period until the new report lands.
    <Box pos="relative" style={{ minWidth: 0, maxWidth: '100%' }}>
      <LoadingOverlay visible={reportQuery.isFetching} overlayProps={{ blur: 2 }} />
      <Stack gap="xl" style={{ minWidth: 0 }}>
        <KpiSections report={report} isDialogsAvailable={isDialogsAvailable} />
        <DynamicsSection report={report} isDialogsAvailable={isDialogsAvailable} />
        <ProductsSection report={report} />
      </Stack>
    </Box>
  );
};
